import React from 'react';
import {
  FiEdit as EditIcon,
  FiNavigation as NavigationIcon,
} from 'react-icons/fi';

import { CopyIcon, EyeIcon, TrashIcon } from 'src/components/icons';
import {
  PERM_MOD_SETTINGS_MEMBERS,
  USER_OPERATIONS,
  UserRoles,
} from 'src/constants';
import { PortalConfigContext } from 'src/context';
import { usePlanInfo } from 'src/hooks/usePlanInfo';
import { useUserAccess } from 'src/hooks/useUserAccess';
// TODO: will move out of legacy in the future because it depends on other legacy components
// eslint-disable-next-line no-restricted-imports
import { Action, BaseActionsMenuButton } from 'src/legacy/components/Dropdowns';

import { MemberActionsRendererProps } from './types';

export const MemberActionRenderer = ({
  data,
  handleChangeRole,
  handleChangeClientAccess,
  handleDelete,
  handleResendInvite,
  handleCopyInvite,
  currentUser,
  onActionMenuClick,
}: MemberActionsRendererProps) => {
  const { userRoles, userId: tableRowUserId, clientAccess } = data;
  const { userId: currentUserId } = currentUser || {};
  const portalConfig = React.useContext(PortalConfigContext);
  const canDeleteTeamMember = useUserAccess(
    PERM_MOD_SETTINGS_MEMBERS,
    USER_OPERATIONS.delete,
  );

  const { cancelAtPeriodEnd } = usePlanInfo();
  const isDeleteButtonDisabled = cancelAtPeriodEnd;

  // Derived during render so the menu always reflects the current row/props;
  // storing it in state behind a useEffect risked serving stale actions.
  const actions: Action[] = [];

  if (clientAccess) {
    actions.push({
      name: 'Change client access',
      icon: <EyeIcon />,
      onClick: () => handleChangeClientAccess(data),
    });
  }

  if (userRoles && userRoles.length > 0 && tableRowUserId !== currentUserId) {
    switch (userRoles.at(0)) {
      case UserRoles.InternalAdmin:
        actions.unshift({
          name: 'Set as Staff',
          icon: <EditIcon />,
          onClick: () => handleChangeRole(data, UserRoles.InternalStaff),
        });
        break;
      case UserRoles.InternalStaff:
        actions.unshift({
          name: 'Set as Admin',
          icon: <EditIcon />,
          onClick: () => handleChangeRole(data, UserRoles.InternalAdmin),
        });
        break;
      default:
        break;
    }

    if (data.status === 'Inactive' && handleResendInvite) {
      actions.push({
        name: 'Resend invite',
        icon: <NavigationIcon />,
        onClick: () => handleResendInvite(data),
      });
    }

    if (
      data.status !== 'Active' &&
      handleCopyInvite &&
      !portalConfig?.authPreferences.enableMagicLinks
    ) {
      actions.push({
        name: 'Copy invite link',
        onClick: () => handleCopyInvite(data),
        icon: <CopyIcon />,
      });
    }

    if (canDeleteTeamMember && handleDelete) {
      actions.push({
        name: 'Delete',
        icon: <TrashIcon />,
        onClick: () => handleDelete(data),
        isDelete: true,
        isDisabled: isDeleteButtonDisabled,
      });
    }
  }

  return (
    <BaseActionsMenuButton
      onActionMenuClick={onActionMenuClick}
      actions={actions}
    />
  );
};
